import { createContext, useCallback, useRef, useState } from 'react';

import { MapSearchContextType, SearchMetaData, ZoneWiseResult } from './types';
import { getDataWithIdAsKey, groupSearchResultByZone } from './utils';

const defaultSearchMetaData = {
    id: '',
    searchTime: 0,
    searchString: ''
};

/**
 * isMapSearch - defines whether map search open or not
 * setIsMapSearch - sets maps search open status
 * zoneWiseResult - stores search result per the zone, with search item id as key to easilt refer
 * setSearchResult - to set result from search api
 * activeSearchId - stores currently selected search item id
 * setActiveSearchId - sets currently selected search item id
 * resetSearch - resets search data
 * closeSearch - closes map search
 */
export const MapSearchContext = createContext<MapSearchContextType>({
    isMapSearch: false,
    setIsMapSearch: () => {},
    zoneWiseResult: {},
    setSearchResult: () => ({}),
    activeSearchId: undefined,
    setActiveSearchId: () => {},
    resetSearch: () => {},
    closeSearch: () => {},
    searchMetaData: {
        current: defaultSearchMetaData
    }
});

/**
 * MapSearchContextProvider provides map search context.
 */
export const MapSearchContextProvider: React.FC = ({ children }) => {
    const [isMapSearch, setIsMapSearch] = useState(false);
    const [zoneWiseResult, setZoneWiseResult] = useState<ZoneWiseResult>({});
    const [activeSearchId, setActiveSearchId] = useState<string>();
    const searchMetaData = useRef<SearchMetaData>(defaultSearchMetaData);

    const setSearchResult = useCallback((region, result) => {
        if (!result) {
            setZoneWiseResult({});
            return {};
        }
        const dataWithIdAsKey = getDataWithIdAsKey(result);
        const zoneWiseResult = groupSearchResultByZone(region, dataWithIdAsKey);
        setZoneWiseResult(zoneWiseResult);
        return zoneWiseResult;
    }, []);

    const resetSearch = () => {
        searchMetaData.current = defaultSearchMetaData;
        setSearchResult(undefined, undefined);
        setActiveSearchId(undefined);
    };

    const closeSearch = () => {
        setIsMapSearch(false);
        resetSearch();
    };

    return (
        <MapSearchContext.Provider
            value={{
                isMapSearch,
                setIsMapSearch,
                zoneWiseResult,
                setSearchResult,
                activeSearchId,
                setActiveSearchId,
                resetSearch,
                closeSearch,
                searchMetaData
            }}
        >
            {children}
        </MapSearchContext.Provider>
    );
};
